import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { ModuleTips } from '../components/onboarding';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, Trash2, Image, FileText, Link2, X, Eye, Sparkles, Loader2, Square, CheckSquare
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Receipts = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptImageUrl, setReceiptImageUrl] = useState(null);
  const [uploadLimits, setUploadLimits] = useState({ maxReceiptSizeKB: 500 });
  const [confirmDeleteReceiptId, setConfirmDeleteReceiptId] = useState(null);
  const [bulkDeleteReceiptsArmed, setBulkDeleteReceiptsArmed] = useState(false);
  const [selectedReceipts, setSelectedReceipts] = useState([]);
  const fileInputRef = useRef(null);
  const ocrFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchUploadLimits();
  }, []);

  const fetchUploadLimits = async () => {
    try {
      const response = await axios.get(`${API}/upload-limits`);
      // Backend returns snake_case; keep the camelCase shape the UI uses
      const kb = Number(response.data?.max_receipt_size_kb ?? response.data?.maxReceiptSizeKB);
      setUploadLimits({ ...response.data, maxReceiptSizeKB: Number.isFinite(kb) && kb > 0 ? kb : 500 });
    } catch (error) {
      // Silently fail - upload limits are not critical
    }
  };

  const fetchData = async () => {
    try {
      const [receiptsRes, txnRes] = await Promise.all([
        axios.get(`${API}/receipts`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/transactions?year=${new Date().getFullYear()}`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      
      setReceipts(receiptsRes.data);
      setTransactions(txnRes.data.filter(t => !t.receipt_id && t.type !== 'income'));
    } catch (error) {
      toast.error(t('toast.failed_to_load_receipts'));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const maxSize = uploadLimits.maxReceiptSizeKB * 1024;
    
    for (const file of files) {
      // Check file size using limits from API
      if (file.size > maxSize) {
        toast.error(t('toast.receipt_too_large', { name: file.name, max: uploadLimits.maxReceiptSizeKB }));
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        await axios.post(`${API}/receipts`, formData, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        });
        toast.success(t('toast.receipt_uploaded', { name: file.name }));
      } catch (error) {
        if (error.response?.status === 413) {
          toast.error(t('toast.file_too_large_server', { name: file.name }));
        } else {
          toast.error(t('toast.receipt_upload_failed', { name: file.name }));
        }
      }
    }

    setUploading(false);
    fetchData();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (receiptId) => {
    if (confirmDeleteReceiptId !== receiptId) {
      setConfirmDeleteReceiptId(receiptId);
      toast.info('Click delete again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDeleteReceiptId(null), 3000);
      return;
    }
    setConfirmDeleteReceiptId(null);
    try {
      await axios.delete(`${API}/receipts/${receiptId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('toast.receipt_deleted'));
      fetchData();
    } catch (error) {
      toast.error(t('toast.failed_to_delete'));
    }
  };

  const handleViewReceipt = async (receipt) => {
    setSelectedReceipt(receipt);
    try {
      const response = await axios.get(`${API}/receipts/${receipt.receipt_id}/image`, {
        headers: getAuthHeaders(),
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      setReceiptImageUrl(url);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error(t('toast.failed_to_load_receipt_image'));
    }
  };

  const handleLinkReceipt = async (receipt) => {
    setSelectedReceipt(receipt);
    setLinkDialogOpen(true);
    
    // Fetch match suggestions if receipt has extracted data
    try {
      const params = new URLSearchParams();
      if (receipt.extracted_amount) {
        params.append('extracted_amount', receipt.extracted_amount);
      }
      if (receipt.extracted_date) {
        params.append('extracted_date', receipt.extracted_date);
      }
      
      const response = await axios.get(`${API}/receipts/${receipt.receipt_id}/match-suggestions?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        // Sort transactions to show suggestions first
        const suggestionIds = new Set(response.data.suggestions.map(s => s.transaction_id));
        const sortedTransactions = [
          ...response.data.suggestions,
          ...transactions.filter(t => !suggestionIds.has(t.transaction_id) && !t.receipt_id && t.type !== 'income')
        ];
        setTransactions(sortedTransactions);
      }
    } catch (error) {
      // If suggestions fail, just show all transactions (fallback behavior)
    }
  };

  const handleLinkToTransaction = async (transactionId) => {
    try {
      await axios.put(`${API}/receipts/${selectedReceipt.receipt_id}/link?transaction_id=${transactionId}`, {}, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success(t('toast.receipt_linked_to_transaction'));
      setLinkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('toast.failed_to_link_receipt'));
    }
  };

  const toggleReceiptSelect = (id) => {
    setSelectedReceipts(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const handleSelectAllReceipts = () => {
    setSelectedReceipts(prev => prev.length === receipts.length ? [] : receipts.map(r => r.receipt_id));
  };
  const handleBulkDeleteReceipts = async () => {
    if (selectedReceipts.length === 0) return;
    if (!bulkDeleteReceiptsArmed) {
      setBulkDeleteReceiptsArmed(true);
      toast.info(`Click again to delete ${selectedReceipts.length} receipts`, { duration: 3000 });
      setTimeout(() => setBulkDeleteReceiptsArmed(false), 3000);
      return;
    }
    setBulkDeleteReceiptsArmed(false);
    try {
      const response = await axios.post(`${API}/receipts/bulk-delete`, selectedReceipts, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(t('toast.deleted_docs', { count: response.data.count }));
      setSelectedReceipts([]);
      fetchData();
    } catch (error) { toast.error(t('toast.failed_to_delete')); }
  };

  const handleOcrUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = uploadLimits.maxReceiptSizeKB * 1024;
    if (file.size > maxSize) {
      toast.error(t('toast.receipt_size_limit', { max: uploadLimits.maxReceiptSizeKB }));
      return;
    }

    setOcrProcessing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/receipts/ocr-and-create`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      const ocr = response.data?.ocr_data || {};
      const vendor = ocr.vendor || response.data?.transaction?.description || 'Receipt';
      const total = Number(ocr.total ?? response.data?.transaction?.amount ?? 0);
      toast.success(
        <div data-testid="ocr-success-toast">
          <p className="font-semibold">Receipt processed!</p>
          <p className="text-sm">
            {vendor}: ${total.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500">
            {response.data?.duplicate
              ? 'Matched an existing transaction — no duplicate created'
              : 'Transaction created as draft'}
          </p>
        </div>
      );
      fetchData();
    } catch (error) {
      toast.error(t('toast.failed_to_process_receipt_try_uploading_manually'));
    } finally {
      setOcrProcessing(false);
      if (ocrFileInputRef.current) {
        ocrFileInputRef.current.value = '';
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    return (bytes / 1024).toFixed(1) + ' KB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="receipts-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">{t('receipts.title')}</h1>
            <p className="text-muted-foreground">{t('receipts.subtitle', 'Upload and organize your receipt images')}</p>
          </div>
          
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
              data-testid="receipt-file-input"
            />
            <input
              type="file"
              ref={ocrFileInputRef}
              onChange={handleOcrUpload}
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
              data-testid="ocr-file-input"
            />
            <Button 
              onClick={() => ocrFileInputRef.current?.click()}
              disabled={ocrProcessing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="ocr-upload-btn"
            >
              {ocrProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {ocrProcessing ? t('receipts.processing') : t('receipts.smart_upload', 'Smart Upload (AI)')}
            </Button>
            <Button 
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              data-testid="upload-receipt-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? t('common.loading') : t('receipts.manual_upload', 'Manual Upload')}
            </Button>
          </div>
        </div>

        {/* Upload Info */}
        <Card className="shadow-soft border-border/40 bg-emerald-50 border-emerald-200" data-testid="upload-info-card">
          <CardContent className="p-4 flex gap-3">
            <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-emerald-800">
                <strong>{t('receipts.smart_upload', 'Smart Upload (AI)')}:</strong> {t('receipts.smart_upload_desc', 'Automatically extracts vendor, amount, and date from receipts and creates a transaction.')}{' '}
                {t('receipts.max_size', { size: uploadLimits.maxReceiptSizeKB })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {receipts.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleSelectAllReceipts} data-testid="select-all-receipts">
              {selectedReceipts.length === receipts.length && receipts.length > 0 ? <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</> : <><Square className="w-4 h-4 mr-1" /> Select All</>}
            </Button>
            {selectedReceipts.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleBulkDeleteReceipts} data-testid="bulk-delete-receipts">
                <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedReceipts.length} Selected
              </Button>
            )}
          </div>
        )}

        {/* Receipts Grid */}
        {receipts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="receipts-grid">
            {receipts.map((receipt) => (
              <Card 
                key={receipt.receipt_id} 
                className={`shadow-soft border-border/40 overflow-hidden group ${selectedReceipts.includes(receipt.receipt_id) ? 'ring-2 ring-primary' : ''}`}
                data-testid={`receipt-${receipt.receipt_id}`}
              >
                <div className="aspect-square bg-muted/50 relative flex items-center justify-center">
                  <div className="absolute top-2 left-2 z-10">
                    <Checkbox checked={selectedReceipts.includes(receipt.receipt_id)} onCheckedChange={() => toggleReceiptSelect(receipt.receipt_id)} />
                  </div>
                  {receipt.filename.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                    <Image className="w-12 h-12 text-muted-foreground/50" />
                  ) : (
                    <FileText className="w-12 h-12 text-muted-foreground/50" />
                  )}
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={() => handleViewReceipt(receipt)}
                      data-testid={`view-receipt-${receipt.receipt_id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={() => handleLinkReceipt(receipt)}
                      data-testid={`link-receipt-${receipt.receipt_id}`}
                    >
                      <Link2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleDelete(receipt.receipt_id)}
                      data-testid={`delete-receipt-${receipt.receipt_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Linked badge */}
                  {receipt.transaction_id && (
                    <Badge className="absolute top-2 right-2 bg-emerald-500">
                      <Link2 className="w-3 h-3 mr-1" />
                      Linked
                    </Badge>
                  )}
                </div>
                
                <CardContent className="p-3">
                  <p className="text-sm font-medium truncate" title={receipt.filename}>
                    {receipt.filename}
                  </p>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{formatFileSize(receipt.file_size)}</span>
                    <span>{formatDate(receipt.uploaded_at)}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="shadow-soft border-border/40" data-testid="empty-receipts">
            <CardContent className="p-12 text-center">
              <Image className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-medium mb-2">No receipts yet</h3>
              <p className="text-muted-foreground mb-4">
                Upload your first receipt to start organizing your expenses
              </p>
              <Button onClick={() => fileInputRef.current?.click()}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Receipt
              </Button>
            </CardContent>
          </Card>
        )}

        {/* View Receipt Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{selectedReceipt?.filename}</DialogTitle>
            </DialogHeader>
            {receiptImageUrl && (
              <div className="max-h-[70vh] overflow-auto">
                <img 
                  src={receiptImageUrl} 
                  alt="Receipt" 
                  className="w-full h-auto"
                  data-testid="receipt-image"
                />
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Link Receipt Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('receipts.link_to_transaction', 'Link Receipt to Transaction')}</DialogTitle>
              <DialogDescription>
                {selectedReceipt?.extracted_amount && selectedReceipt?.extracted_date 
                  ? t('receipts.ai_suggested_matches', 'AI suggested matches based on amount and date')
                  : t('receipts.select_transaction', 'Select a transaction to link this receipt to')
                }
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-96 overflow-auto">
              {transactions.length > 0 ? (
                transactions.map((txn) => (
                  <div 
                    key={txn.transaction_id}
                    className={`p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors ${
                      txn.score && txn.score > 50 ? 'border-emerald-300 bg-emerald-50/50' : ''
                    }`}
                    onClick={() => handleLinkToTransaction(txn.transaction_id)}
                    data-testid={`link-txn-${txn.transaction_id}`}
                  >
                    {txn.score && txn.score > 0 && (
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <Sparkles className="w-3 h-3 mr-1" />
                          {txn.score}% match
                        </Badge>
                        {txn.match_reasons && txn.match_reasons.map((reason, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {reason}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium truncate">{txn.description}</p>
                        <p className="text-sm text-muted-foreground">{txn.date}</p>
                      </div>
                      <span className="mono font-semibold">${txn.amount.toFixed(2)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No unlinked transactions available
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="receipts" />
  </>
  );
};

export default Receipts;
